import type { ErrorResponse } from '@mnc/contracts';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export const UNAUTHORIZED_EVENT = 'mnc:unauthorized';

async function request<T>(method: string, url: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const isForm = body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: { accept: 'application/json', ...(body !== undefined && !isForm ? { 'content-type': 'application/json' } : {}), ...(init.headers ?? {}) },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      ...init,
    });
  } catch {
    throw new ApiError(0, 'network', 'network');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const err = (json ?? {}) as Partial<ErrorResponse>;
    if (res.status === 401 && !url.startsWith('/api/auth/login')) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError(res.status, err.code ?? 'error', err.message ?? res.statusText, err.details);
  }
  return json as T;
}

/**
 * Downloads a file the API generated (Excel exports). The session cookie authorises it, so a plain link would
 * also work, but going through fetch lets a 403/404 surface as a toast instead of a broken page, and keeps the
 * server's file name (RFC 5987 `filename*`, so Arabic names survive).
 */
async function download(url: string, body?: unknown): Promise<string> {
  let res: Response;
  try {
    res = await fetch(url, body === undefined ? { method: 'GET', credentials: 'include' } : { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'network', 'network');
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as Partial<ErrorResponse>;
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    throw new ApiError(res.status, err.code ?? 'error', err.message ?? res.statusText, err.details);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const plain = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const fileName = star ? decodeURIComponent(star) : (plain ?? 'export.xlsx');
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
  return fileName;
}

export const api = {
  download,
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body),
  delete: <T>(url: string) => request<T>('DELETE', url),
  upload: <T>(url: string, file: File, field = 'file') => {
    const fd = new FormData();
    fd.append(field, file);
    return request<T>('POST', url, fd);
  },
};

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}
